import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { LoggerService } from '@wdpr/ra-angular-logger';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';

import { ConfigService } from '@app/core/config.service';
import { LocaleParts } from './locale-parts.interface';
import { LOCALES } from './locale.constants';
import { GeolocationService } from '../geolocation/geolocation.service';
import { of } from 'rxjs/observable/of';

@Injectable()
export class LocaleService {
    private localeCookieNames: any;
    private LACDLocales = ['es-ar', 'es-cl', 'es-co', 'es-pe', 'es-mx', 'pt-br'];
    private defaultLocales = ['en-us', 'en-gb', 'en-eu'];

    private UK_CONTENT = 'en_GB';
    private EU_CONTENT = 'en_EU';
    private AS_CONTENT = 'en_AS';
    private akaLocaleCookieExists = document.cookie.match(/localeCookie_jar_aka/);
    private SUPPORTED_LANGS: any = {
        'default': 'en', // Default
        // By Country ISO Code
        'usa': 'en_US',
        'can': 'en_CA',
        'esus': 'es_US',
        'gib': this.UK_CONTENT,
        'imn': this.UK_CONTENT,
        'gbr': this.UK_CONTENT,
        'ggy': this.UK_CONTENT,
        'jey': this.UK_CONTENT,
        'esp': this.EU_CONTENT,
        'nld': this.EU_CONTENT,
        'deu': this.EU_CONTENT,
        'irl': this.EU_CONTENT,
        'ita': this.EU_CONTENT,
        'fra': this.EU_CONTENT,
        'bel': this.EU_CONTENT,
        'nor': this.EU_CONTENT,
        'swe': this.EU_CONTENT,
        'dnk': this.EU_CONTENT,
        'grc': this.EU_CONTENT,
        'prt': this.EU_CONTENT,
        'icl': this.EU_CONTENT,
        'che': this.EU_CONTENT,
        'mlt': this.EU_CONTENT,
        'pol': this.EU_CONTENT,
        'aut': this.EU_CONTENT,
        'rou': this.EU_CONTENT,
        'fin': this.EU_CONTENT,
        'cze': this.EU_CONTENT,
        'cyp': this.EU_CONTENT,
        'hrv': this.EU_CONTENT,
        'est': this.EU_CONTENT,
        'hun': this.EU_CONTENT,
        'svk': this.EU_CONTENT,
        'alb': this.EU_CONTENT,
        'svn': this.EU_CONTENT,
        'mco': this.EU_CONTENT,
        'bgr': this.EU_CONTENT,
        'reu': this.EU_CONTENT,
        'sau': this.AS_CONTENT,
        'kwt': this.AS_CONTENT,
        'isr': this.AS_CONTENT,
        'are': this.AS_CONTENT,
        'qat': this.AS_CONTENT,
        'egy': this.AS_CONTENT,
        'bhr': this.AS_CONTENT,
        'tur': this.AS_CONTENT,
        'lbn': this.AS_CONTENT,
        'jor': this.AS_CONTENT,
        'omn': this.AS_CONTENT,
        'irq': this.AS_CONTENT
    };

    private SUPPORTED_CURRENCIES = {
        'en': 'USD',
        'en_GB': 'GBP',
        'en_CA': 'USD',
        'en_EU': 'EUR',
        'en_AS': 'USD'
    };

    private enUS = 'en_US';
    private esUS = 'es_US';
    private ptBR = 'pt_BR';
    private deDE = 'de_DE';
    private esAR = 'es_AR';
    private esMX = 'es_MX';
    private esPE = 'es_PE';
    private esCO = 'es_CO';
    private esCL = 'es_CL';

    constructor(
        private cookieService: CookieService,
        private configService: ConfigService,
        private loggerService: LoggerService,
        private geolocationService: GeolocationService
    ) {
        if (this.akaLocaleCookieExists) {
            // This code will be removed once DCL INTL reaches production,
            // we do this to avoid issues in environments where akamai settings don't exist
            // If locale comes in url so DCL is INTL therefore some values must change:
            // Default lang is en_US and deu (Germany) should localize as de_DE not en_EU

            this.SUPPORTED_LANGS.default = 'en_US';
            this.SUPPORTED_LANGS.usa = 'en_US';
            this.SUPPORTED_LANGS.deu = 'de_DE';
            this.SUPPORTED_LANGS.bra = 'pt_BR';
            this.SUPPORTED_LANGS.arg = 'es_AR';
            this.SUPPORTED_LANGS.mex = 'es_MX';
            this.SUPPORTED_LANGS.per = 'es_PE';
            this.SUPPORTED_LANGS.col = 'es_CO';
            this.SUPPORTED_LANGS.chl = 'es_CL';
            this.SUPPORTED_CURRENCIES[this.enUS] = 'USD';
            this.SUPPORTED_CURRENCIES[this.esUS] = 'USD';
            this.SUPPORTED_CURRENCIES[this.ptBR] = 'BRL';
            this.SUPPORTED_CURRENCIES[this.deDE] = 'EUR';
            this.SUPPORTED_CURRENCIES[this.esAR] = 'ARS';
            this.SUPPORTED_CURRENCIES[this.esMX] = 'MXN';
            this.SUPPORTED_CURRENCIES[this.esPE] = 'PEN';
            this.SUPPORTED_CURRENCIES[this.esCO] = 'COP';
            this.SUPPORTED_CURRENCIES[this.esCL] = 'CLP';
        }
        this.localeCookieNames = this.configService.getValue('localeCookieNames');
    }

    /**
     * Gets the locale from the language selection cookie.
     * @returns {string} the locale string.
     */
    getLocale() {
        const languageCookieValue = this.getLanguageCookie();
        let result;

        if (languageCookieValue) {
            result = languageCookieValue.preferredLanguage || languageCookieValue.preferredRegion || languageCookieValue.contentLocale;
        }

        if (!result) {
            return new Promise(resolve => {
                this.geolocationService.getGeoCode().subscribe(geodata => {
                    let selectedLocale = this.SUPPORTED_LANGS.default;
                        // expiration = this.getExpiration(); // NOT NEEDED
                    // Enter geolocation promise
                    // Find supported language, if available
                    if (this.SUPPORTED_LANGS.hasOwnProperty(geodata.countryisocode)) {
                        selectedLocale = this.SUPPORTED_LANGS[geodata.countryisocode];
                    } else if (this.SUPPORTED_LANGS.hasOwnProperty(geodata.continent)) {
                        selectedLocale = this.SUPPORTED_LANGS[geodata.continent];
                    }

                    /**
                     *  Not needed for now.
                     *
                     *
                    // Set supported currency.
                    var selectedCurrency = this.SUPPORTED_CURRENCIES[selectedLocale] !== undefined ?
                        this.SUPPORTED_CURRENCIES[selectedLocale] :
                        this.SUPPORTED_CURRENCIES[this.SUPPORTED_LANGS.default];
                     */

                    /**
                     * put cookies? they should be availbe after reload anyway.
                     *
                     *
                    // Set contentLocale cookie (Matching logic from PEP)
                    // TODO: set contentLocale to selectedLocale when DCL is internationalized
                    let localeObj = {
                        contentLocale: 'en',
                        version: '2',
                        precedence: 0,
                        localeCurrency: selectedCurrency,
                        preferredRegion: selectedLocale
                    };

                    wdprCookies.putObject(localeCookieService.getLocaleCookieName(), localeObj,
                        {expires: expiration, secure: location.protocol === 'https:'});
                     */

                    resolve(selectedLocale);
                });
            });
        }


        return Promise.resolve(result);
    }

    /*
    * Creates an expiration date to be current time + 90 days (in seconds) (matching logic from PEP)
    */
    // getExpiration() {
    //     return new Date((new Date()).getTime() + 1000 * 7776000);
    // }

    /**
     * Gets the value of the language selection cookie.
     * @returns {any} the cookie value object or undefined
     */
    getLanguageCookie(): any {
        return this.getCookieObjectValue(
            get('akamai.languageSelection', this.localeCookieNames), get(this.localeCookieNames, 'default.languageSelection')
        );
    }

    /**
     * Gets the value of the locale cookie.
     * @returns {any} the cookie value object or undefined
     */
    getLocaleCookie(): any {
        return this.getCookieObjectValue(get(this.localeCookieNames, 'akamai.locale'), get(this.localeCookieNames, 'default.locale'));
    }

    /**
     * Check to see if english is the selected language
     * @returns {<=>}
     */
    // isLocaleEnglish() {
    //     const locale = this.getLocale();

    //     // If we can't pull the value from the cookie then return -1
    //     if (!locale) {
    //         return -1;
    //     }

    //     return locale === LOCALES.en_US ? 1 : 0;
    // }

    /**
     * Checks if guest visits the page using a latin american locale.
     */
    // isLACDRegion() {
    //     return this.LACDLocales.includes(this.getLocale());
    // }

    /**
     * Check to see if this is a default english locale
     */
    // isDefaultLocale() {
    //     return this.defaultLocales.includes(this.getNormalizedLocale());
    // }

    /**
     * Get the lang and locale from the full locale string (e.g., `en_US`, `es-us`, etc.)
     */
    // getLocaleParts(): LocaleParts | null {
    //     // Get the current guest locale
    //     const localeString = this.getNormalizedLocale();

    //     if (!localeString) {
    //         return;
    //     }

    //     // Split into parts (thanks, normalization! 😁)
    //     const localeSplit = localeString.split('-');

    //     // Get the lang and locale
    //     // If we have a locale string that doesn't fit the "mormal" pattern (e.g., `jp`, `pt`)
    //     // then we'll just use it for both lang and locale values
    //     const lang = localeSplit[0];
    //     const locale = localeSplit.length > 1 ? localeSplit[1] : localeSplit[0];

    //     return {
    //         language: lang,
    //         locale: locale
    //     };
    // }

    /**
     * Gets the cookie value as object.
     * Checks the fallback cookie name if the fist one doesn't exist.
     * @param cookieName the cookie name.
     * @param defaultCookieName the fallback cookie name.
     */
    private getCookieObjectValue(cookieName, defaultCookieName): Object {
        let result;
        let cookieValue = this.cookieService.get(cookieName);
            cookieValue = cookieValue ?
                cookieValue : this.cookieService.get(defaultCookieName);

        // Make sure there's something in the cookie to parse first
        if (isEmpty(cookieValue)) {
            return result;
        }

        try {
            result = JSON.parse(cookieValue);
        } catch (e) {
            this.loggerService
                .error('locale-service:::Error parsing the locale cookie: is not a valid object');
        }

        return result;
    }

    /**
     * Normalize the locale string to `xx-xx` format to make it easier to parse
     */
    // private getNormalizedLocale(): string {
    //     const locale = this.getLocale();
    //     let result;

    //     if (locale) {
    //         result = locale.toLowerCase().replace('_', '-');
    //     }

    //     return result;
    // }

}
